"use client";

import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/shared/ui/button";
import { PaletteFooter } from "../components/PaletteFooter";
import type { Command, PaletteCtx, Result, Verb } from "../types";

export function ConfirmPage({ ctx, command }: { ctx: PaletteCtx; command: Command }) {
  return (
    <ConfirmShell
      ctx={ctx}
      title={command.confirm?.title ?? command.title}
      body={command.confirm?.body}
      cta={command.confirm?.cta ?? "Confirm"}
      onConfirm={async () => {
        await command.run(ctx);
        ctx.close();
      }}
    />
  );
}

export function VerbConfirmPage({ ctx, verb, result }: { ctx: PaletteCtx; verb: Verb; result: Result }) {
  return (
    <ConfirmShell
      ctx={ctx}
      title={verb.confirm?.title ?? `${verb.title}: ${result.title}`}
      body={verb.confirm?.body}
      cta={verb.confirm?.cta ?? verb.title}
      destructive={verb.destructive}
      onConfirm={async () => {
        await verb.run(result.entity, ctx);
        ctx.close();
      }}
    />
  );
}

function ConfirmShell({
  ctx,
  title,
  body,
  cta,
  destructive,
  onConfirm,
}: {
  ctx: PaletteCtx;
  title: string;
  body?: string;
  cta: string;
  destructive?: boolean;
  onConfirm: () => Promise<void> | void;
}) {
  return (
    <>
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3">
        <button
          type="button"
          onClick={ctx.pop}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground outline-none transition-[color,background-color] duration-(--dur-fast) hover:bg-accent hover:text-foreground focus-visible:ring-(length:--ring-w) focus-visible:ring-ring"
          aria-label="Back"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-caption font-semibold uppercase tracking-wider text-muted-foreground">Confirm</div>
      </div>

      <div className="flex-1 flex flex-col items-start justify-center px-10 gap-3">
        <div className="text-xl font-semibold leading-tight">{title}</div>
        {body && <div className="text-sm text-muted-foreground max-w-md">{body}</div>}
        <div className="flex items-center gap-2 mt-4">
          <Button variant="ghost" onClick={ctx.pop}>
            Cancel
          </Button>
          <Button variant={destructive ? "destructive" : "default"} onClick={() => void onConfirm()}>
            {cta}
          </Button>
        </div>
      </div>

      <PaletteFooter />
    </>
  );
}
